import React, { useMemo, useState, useCallback, useEffect } from 'react';
import ReactSelect, { components } from "react-select";

import { useSite } from '../../../util/sites';
import PresetEventEmitter, { presetEventNames } from '../../../util/presetEventEmitter';

import sitesSelectStyles from './SitesSelect.module.css';

const InputOption = ({ getStyles, Icon, isDisabled, isFocused, isSelected, children, innerProps, ...rest }) => {
  const [isActive, setIsActive] = useState(false);
  const onMouseDown = () => setIsActive(true);
  const onMouseUp = () => setIsActive(false);
  const onMouseLeave = () => setIsActive(false);

  // styles
  let bg = "transparent";
  if (isFocused) bg = "#eee";
  if (isActive) bg = "#B2D4FF";

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex ",
		fontWeight: rest.data.value === '_ALL' ? 'bold' : 'regular'
  };

  // prop assignment
  const props = {
    ...innerProps,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    style
  };

	return (
    <components.Option key={rest.data.value} {...rest} isDisabled={isDisabled} isFocused={isFocused} isSelected={isSelected} getStyles={getStyles} innerProps={props}>
      {children}
    </components.Option>
  );
};

const MultiValueContainer = props => {
	if(props.data.value === '_ALL') { return null }
  return (
		<components.MultiValueContainer {...props} />
  );
};

const filterOptions = ({ data }, input) => {
	input = input.trim();
  if(input) {
		if(data.value === '_ALL') { return false }
    return data.label.toLowerCase().includes(input.toLowerCase()) || data.value?.company?.toLowerCase().includes(input.toLowerCase());
  }
  return true;
};

const styleOptionByOP = (site) => {
	switch (site.operation_status) {
		case 1:
			return "#00757e !important";
		case 2:
			return "#ff8500 !important";
		case 4:
			return "#8dc63f !important";
		default:
			return "inherit";
	}
};

const selectStyles = {
	container: (provided) => ({
		...provided,
		width: '100%'
	}),
	control: (provided) => ({
		...provided,
		borderRadius: '5px'
	}),
	option: (provided, state) => ({
		...provided,
		color: styleOptionByOP(state.data.value)
	}),
	valueContainer: () => ({
		maxHeight: "88px",
		overflowY: "auto",
		display: "flex",
		flex: "1 1 0%",
		flexWrap: "wrap",
		padding: '0.2em 0.7em',
		fontSize: '1.5rem'
	}),
	placeholder: (provided) => ({
		...provided,
		lineHeight: '1',
		position: 'absolute',
		textTransform: 'uppercase',
		color: '#333'
	})
};

export default function SitesSelect({ value, onChange=async (val)=>console.log('onChange:', val), filterSite, filterClient, filterCountry, isDisabled=false }) {

	const { allSites_Select, selectedSite } = useSite() || {};
	const soloSiteOptions = (!allSites_Select?.length && selectedSite?.value) ? [
		{
			label: selectedSite.label,
			clientID: selectedSite.value.client_id,
			options: [ selectedSite ]
		}
	] : null;
	const clientOptions = soloSiteOptions || allSites_Select || [];

	const [ searchInput, setSearchInput ] = useState('');

	const handleOnInputChange = useCallback((str, { action }) => {
		if(action === 'set-value') { return }
		setSearchInput(str);
	}, []);

	const createGroup = useCallback((groupName, options) => {
		return {
			label: (() => {
				return (
					<div
						onClick={() => {
							// when clicking on group name, select all options in this group
							if(!options.every(option => value.find(site => site.site_id === option.value.site_id))) {
								onChange(value.concat(options.filter(option => !value.find(site => site.site_id === option.value.site_id)).map(siteOptions => siteOptions.value)));
								setSearchInput('');
							}
							// when clicking on group name again, de-select all options in this group
							if(options.every(option => value.find(site => site.site_id === option.value.site_id))) {
								onChange(value.filter(site => !options.find(_option => _option.value.site_id === site.site_id)));
								setSearchInput('');
							}
						}}
					>
						{/* for solo site, groupName will be empty string */}
						{groupName || options[0].label}
					</div>
				);
			})(),
			options: options.filter(option => !value?.find(site => site.site_id === option.value.site_id)) // filter out selected sites
				// filter countries
				.filter(option => filterCountry ? option.value.country === filterCountry : true)
				// fitler clients
				.filter(option => typeof(filterClient) === 'number' ? option.value.client_id === filterClient : true)
				.filter(option => typeof(filterClient) === 'number' ? option.value.operation_status > 1 : true) // if user is client, only show MSA/COD sites
				// filter sites
				.filter(option => filterSite ? filterSite.includes(option.value.site_id) : true)
		};
	}, [value, onChange, filterCountry, filterClient, filterSite]);
	
	const filterOutAllOption = useCallback((options) => options?.length ? options.filter(option => option.value !== '_ALL') : [], []);

	const allSites = useMemo(() => clientOptions.map(clientOption => clientOption.options.map(siteOption => siteOption.value)).flat(1), [ clientOptions ]);
	const allShownSites = useMemo(() => {
		return allSites
			// filter countries
			.filter(site => filterCountry ? site.country === filterCountry : true)
			// fitler clients
			.filter(site => typeof(filterClient) === 'number' ? site.client_id === filterClient : true)
			.filter(site => typeof(filterClient) === 'number' ? site.operation_status > 1 : true) // if user is client, only show MSA/COD sites
			// filter sites
			.filter(site => filterSite ? filterSite.includes(site.site_id) : true)
	}, [allSites, filterClient, filterCountry, filterSite]);
	const allOption = useMemo(() => ({ label: 'All Sites', value: '_ALL' }), []);
	const options = useMemo(() => {
		let options;
		// for GY user, show grouped options
		if(typeof(filterClient) !== 'number') {
			options = clientOptions.map(client => createGroup(client.label, client.options));
		}
		// for client, show options without group
		if(typeof(filterClient) === 'number') {
			options = allShownSites
				.filter(site => !value.find(_site => site.site_id === _site.site_id)) // filter out selected sites
				.map(site => ({ label: site.site_name, value: site }));
		}
		// add select all button
		if(!value || value.length <= allShownSites.length - 2) {
			options.unshift(allOption);
		}
		// if solo site, hide client name
		options = options.map(option => {
			if(option.options?.length === 1 && option.options[0].value.is_solo) {
				return ({ ...option, label: null });
			}
			return option;
		});
		return options;
	}, [filterClient, clientOptions, createGroup, allShownSites, value, allOption]);

	const handleSelectChange = useCallback((selectedOptions, event) => {
		const clearPreset = true;
		setSearchInput('');
		if(!selectedOptions?.length) { return onChange([], { clearPreset }) } // if user clear all options
		selectedOptions = filterOutAllOption(selectedOptions); // filter out all option
		// event perform on "All" option
		if(event.option?.value === '_ALL') {
			if(event.action === 'select-option' && filterOutAllOption(selectedOptions).length === allShownSites.length) {
				onChange([], { clearPreset });
			}
			if(event.action === 'select-option' && filterOutAllOption(selectedOptions).length !== allShownSites.length) {
				onChange(allShownSites, { clearPreset });
			}
			if(event.action === 'deselect-option' || event.action === 'remove-value') { onChange([], { clearPreset }) }
			return;
		}
		// event perform on other options
		if(event.option?.value !== '_ALL') {
			const selectedSites = selectedOptions.map(siteOption => siteOption.value);
			onChange(selectedSites, { clearPreset });
		}
	}, [filterOutAllOption, onChange, allShownSites]);

	// PresetEvents subscriber on mount
	useEffect(() => {
		const callback = async ({ siteIds, reloadP10Form=false }={}) => {
			const newSites  = allShownSites.filter(site => siteIds?.includes(site.site_id));
			if(!newSites) { return onChange([]) }
			await onChange(newSites, { reloadP10Form });
		};
		PresetEventEmitter.subscribe(presetEventNames.SET_SELECT_SITE, callback); // emitted by preset select
		return () => PresetEventEmitter.unsubscribe(presetEventNames.SET_SELECT_SITE, callback);
	}, [allShownSites, onChange]);

	// dynamic vars
	const selectedOptions = useMemo(() => {
		const selectedOptions = value.map(site => ({ label: site.site_name, value: site }));
		if(value.length === allShownSites.length) {
			selectedOptions.unshift(allOption);
		}
		return selectedOptions;
	}, [allOption, allShownSites, value]);

	return (
		<ReactSelect
			inputValue={searchInput}
			options={options}
			value={selectedOptions}
			isSearchable={true}
			isMulti
			filterOption={filterOptions}
			closeMenuOnSelect={false}
      hideSelectedOptions={false}
			className={sitesSelectStyles.sitesSelect}
			components={{ Option: InputOption, MultiValueContainer: MultiValueContainer }}
			onInputChange={handleOnInputChange}
			styles={selectStyles}
			onChange={handleSelectChange}
			placeholder="Select Site(s)"
			isDisabled={isDisabled}
		/>
	);
}